import contentful from 'clients/contentful'
import {
  createMetaTags,
  createSlug,
  getMainCategories,
  getRelatedCategories,
} from 'utilities/blog'
import * as R from 'ramda'
import { getStaticProps as getIndexPageProps } from './index'

export { default } from './index'

const uniqueBlogLinks = ({
  latestAuthorPostsRes,
  relatedRes,
  latestPostsRes,
}) => {
  const latestAuthorPosts = latestAuthorPostsRes.items
  let listedPostsIds = latestAuthorPosts.map(({ sys }) => sys.id)

  const relatedPosts = R.take(
    3,
    relatedRes.items.filter(({ sys }) => !listedPostsIds.includes(sys.id))
  )
  listedPostsIds = [...listedPostsIds, ...relatedPosts.map(({ sys }) => sys.id)]

  const latestPosts = R.take(
    3,
    latestPostsRes.items.filter(({ sys }) => !listedPostsIds.includes(sys.id))
  )

  return { latestAuthorPosts, relatedPosts, latestPosts }
}

export async function getStaticProps({ params }) {
  const [catRes, topNavRes, footerRes] = await Promise.all([
    contentful.getEntries({
      content_type: 'category',
      order: '-sys.createdAt',
    }),
    contentful.getEntries({
      content_type: 'blogTopNavigationBar',
      include: 3,
    }),
    contentful.getEntries({
      content_type: 'blogFooter',
      include: 3,
    }),
  ])

  const mainCategories = getMainCategories(catRes.items)

  const isPostPage =
    params.slug.length === 1 &&
    !mainCategories.find(c => c.fields.slug === params.slug[0])

  if (isPostPage) {
    const blogPostRes = await contentful.getEntries({
      content_type: 'newBlogPost',
      'fields.slug[in]': params.slug[0],
      order: '-sys.createdAt',
      include: 10,
    })

    if (!blogPostRes.items[0]) return { notFound: true }

    const postCategoryIds = blogPostRes.items.reduce(
      (acc, { fields }) =>
        (acc = acc.concat(
          fields.categories ? fields.categories.map(({ sys }) => sys.id) : []
        )),
      []
    )

    const [latestAuthorPostsRes, relatedRes, latestPostsRes] =
      await Promise.all([
        contentful.getEntries({
          content_type: 'newBlogPost',
          'fields.author.sys.contentType.sys.id': 'author',
          'fields.author.fields.slug[in]':
            blogPostRes.items[0].fields.author.fields.slug,
          'fields.slug[nin]': 'editorial-guidelines',
          'fields.slug[ne]': params.slug[0],
          limit: 5,
          order: '-sys.createdAt',
        }),
        contentful.getEntries({
          content_type: 'newBlogPost',
          'fields.categories.sys.id[in]': postCategoryIds.join(','),
          'fields.slug[ne]': params.slug[0],
          limit: 8,
          order: '-sys.createdAt',
        }),
        contentful.getEntries({
          content_type: 'newBlogPost',
          'fields.slug[nin]': 'editorial-guidelines',
          'fields.slug[ne]': params.slug[0],
          limit: 11,
          order: '-sys.createdAt',
        }),
      ])

    const { latestAuthorPosts, relatedPosts, latestPosts } = uniqueBlogLinks({
      latestAuthorPostsRes,
      relatedRes,
      latestPostsRes,
    })

    return {
      props: {
        topNav: topNavRes.items[0],
        footer: footerRes.items[0],
        post: blogPostRes.items[0],
        metadata: createMetaTags(blogPostRes.items[0], params.slug),
        relatedPosts,
        latestAuthorPosts,
        latestPosts,
      },
      revalidate: 10,
    }
  }

  const [templateRes, templateFallbackRes, blogPostRes] = await Promise.all([
    contentful.getEntries({
      content_type: 'blogPageTemplate',
      'fields.category.sys.contentType.sys.id': 'category',
      'fields.category.fields.slug[in]': params.slug[params.slug.length - 1],
      include: 3,
    }),
    contentful.getEntries({
      content_type: 'blogPageTemplate',
      'fields.name': 'Fallback Page',
      include: 3,
    }),
    contentful.getEntries({
      content_type: 'newBlogPost',
      'fields.slug[nin]': 'editorial-guidelines',
      order: '-sys.createdAt',
      include: 10,
    }),
  ])

  const relatedCategories = getRelatedCategories(
    params.slug[params.slug.length - 1],
    catRes.items
  )
  const otherCategories = catRes.items.filter(
    ({ fields }) =>
      fields.parent?.fields.slug === params.slug[0] &&
      fields.slug !== params.slug[1]
  )
  const category = catRes.items.find(
    ({ fields }) => fields.slug === params.slug[params.slug.length - 1]
  )

  const postsBySlug = {}
  let categoryName = ''

  const postsByAllCategories = R.reduce(
    (acc, { fields, sys }) => {
      R.map(
        ({ fields: f }) => {
          if (!acc[f.name]) {
            acc[f.name] = { name: f.name, slug: createSlug(f), posts: [] }
            postsBySlug[f.slug] = []
            if (f.slug === params.slug[params.slug.length - 1])
              categoryName = f.name
          }
          if (acc[f.name].posts.length < 3) {
            acc[f.name].posts.push({ fields, sys })
          }
          postsBySlug[f.slug].push({ fields, sys })
        },
        R.filter(
          ({ fields: f }) =>
            R.includes(
              f.slug,
              R.map(({ fields: f }) => f.slug, relatedCategories)
            ),
          fields.categories || []
        )
      )
      return acc
    },
    {},
    blogPostRes.items
  )

  const { [category.fields.name]: _, ...postsByCategory } = postsByAllCategories

  if (!postsByCategory || !postsBySlug[params.slug[params.slug.length - 1]]) {
    return { notFound: true }
  }

  return {
    props: {
      title: categoryName,
      topNav: topNavRes.items[0],
      template: templateRes.items[0] || templateFallbackRes.items[0] || {},
      footer: footerRes.items[0],
      categories: catRes.items,
      otherCategories,
      isHomepage: false,
      featuredPosts: postsBySlug[params.slug[params.slug.length - 1]],
      postsByCategory,
      metadata: createMetaTags(
        {
          fields: {
            title: `${categoryName}`,
            metaDescription:
              'Expert fitness, nutrition, & wellness advice from the certified trainers at Kickoff and our partners.',
            type: 'website',
          },
        },
        params.slug
      ),
    },
    revalidate: 10,
  }
}

export async function getStaticPaths() {
  const [catRes, indexPagePropRes] = await Promise.all([
    contentful.getEntries({
      content_type: 'category',
      order: '-sys.createdAt',
    }),
    getIndexPageProps(),
  ])

  // const categories = getMainCategories(res.items)
  const catPaths = catRes.items.map(({ fields }) => createSlug(fields))

  const {
    props: { latestPosts, featuredPosts, postsByCategory },
  } = indexPagePropRes
  const indexPagePosts = [
    ...latestPosts,
    ...featuredPosts,
    ...Object.values(postsByCategory).flatMap(({ posts }) => posts),
  ]
  const postPaths = R.uniq(
    indexPagePosts.map(({ fields }) => `/blog/${fields.slug}`)
  )

  return {
    paths: [...catPaths, ...postPaths],
    fallback: true,
  }
}
